import {fabric} from 'fabric';
import {useCallback, useEffect} from 'react';

const isImageObject = (obj) => obj && (obj.type === 'uYOImage' || obj._element);

export const useShadowImage = (canvas) => {
  const removeAllShadows = useCallback(() => {
    if (!canvas) return;

    const shadows = canvas.getObjects().filter((obj) => obj.isShadow);
    shadows.forEach((shadow) => canvas.remove(shadow));
    canvas.requestRenderAll();
  }, [canvas]);

  const createShadowImage = useCallback(
    (target) => {
      if (!canvas || !isImageObject(target)) return;

      if (!target._originalElement && !target._element) {
        return;
      }

      requestAnimationFrame(() => {
        try {
          const image = new fabric.Image(target._originalElement);
          image.set({
            ...target,
            isShadow: true,
            originalObjectId: target.id,
            opacity: 0.4,
            selectable: false,
            evented: false,
            clipPath: null,
            excludeFromExport: true,
          });

          removeAllShadows();

          canvas.add(image);
          canvas.requestRenderAll();
          target.onDeselect = () => {
            canvas.remove(image);
            canvas.requestRenderAll();
          };
        } catch (error) {
          evite.error('Error creating shadow image:', error);
        }
      });
    },
    [canvas, removeAllShadows]
  );

  const updateShadowPosition = useCallback(
    (target) => {
      if (!canvas || !isImageObject(target)) return;

      const shadow = canvas
        .getObjects()
        .find((obj) => obj.isShadow && obj.originalObjectId === target.id);

      if (shadow) {
        shadow.set({
          left: target.left,
          top: target.top,
          scaleX: target.scaleX,
          scaleY: target.scaleY,
          angle: target.angle,
          width: target.width,
          height: target.height,
          flipX: target.flipX,
          flipY: target.flipY,
          filters: target.filters,
        });

        shadow.applyFilters();
        canvas.renderAll();
      }
    },
    [canvas]
  );

  useEffect(() => {
    if (!canvas) return;

    const handleSelectionCreated = (e) => {
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleSelectionUpdated = (e) => {
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleObjectUpdate = ({target}) => {
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const handleObjectModified = () => {
      const target = canvas.getActiveObject();
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const events = {
      'selection:created': handleSelectionCreated,
      'selection:updated': handleSelectionUpdated,
      'object:moving': handleObjectUpdate,
      'object:scaling': handleObjectUpdate,
      'object:modified': handleObjectModified,
    };

    canvas.on(events);
    return () => {
      canvas.off(events);
      removeAllShadows();
    };
  }, [canvas, createShadowImage, updateShadowPosition, removeAllShadows]);
};
